import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, ArrowRight, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import SalesChatBot from "../components/SalesChatBot";
import { usePageSEO } from "../lib/seo";

const HERO_IMG = "https://images.unsplash.com/photo-1774112168854-3518aa1efe11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxmaW5hbmNpYWwlMjBkaXN0cmljdCUyMHNreXNjcmFwZXJzJTIwZHJhbWF0aWMlMjBza3l8ZW58MHx8fHwxNzc1NTE4MDY4fDA&ixlib=rb-4.1.0&q=85";

function SectionHeading({ children }) {
  return <h2 className="text-2xl sm:text-3xl font-['Outfit'] font-medium tracking-tight text-[#0F172A] mb-6 mt-16">{children}</h2>;
}

function Quote({ children }) {
  return <blockquote className="border-l-4 border-[#0033CC] pl-6 my-8 text-lg sm:text-xl font-['Outfit'] font-medium text-[#0F172A] italic leading-relaxed">{children}</blockquote>;
}

function Outcome({ children }) {
  return (
    <div className="bg-[#E5EDFF] border-l-4 border-[#0033CC] p-4 my-6">
      <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-1">Outcome</p>
      <p className="text-sm font-['IBM_Plex_Sans'] text-[#0F172A] leading-relaxed">{children}</p>
    </div>
  );
}

function PhaseCard({ phase, title, items, result }) {
  const colors = { "0-30": "border-t-emerald-500", "30-90": "border-t-[#0033CC]", "90-120": "border-t-purple-500" };
  return (
    <div className={`border border-slate-200 border-t-2 ${colors[phase] || "border-t-[#0033CC]"} bg-white p-6`}>
      <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-1">{phase} Days</p>
      <h4 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-4">{title}</h4>
      <ul className="space-y-2 mb-4">
        {items.map((item, i) => (
          <li key={`idx-${i}`} className="flex items-start gap-2 text-sm font-['IBM_Plex_Sans'] text-slate-600">
            <span className="text-[#0033CC] mt-0.5">+</span>{item}
          </li>
        ))}
      </ul>
      <div className="bg-slate-50 p-3">
        <p className="text-xs font-['JetBrains_Mono'] text-emerald-700">{result}</p>
      </div>
    </div>
  );
}

export default function CaseStudyPage() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  usePageSEO({ title: "An EMD Transforms Itself Amid Intense Volatility", description: "How an exempt market dealer achieved +34% revenue growth in 120 days using an Agentic AI Growth Engine — without adding headcount.", path: "/client-success/an-emd-transforms-itself-amid-intense-volatility", ogTitle: "Client Success: +34% Revenue Growth in 120 Days | Stansbury AI" });

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="case-study-page">
      {/* Header */}
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <img src="/stansbury-logo.png" alt="Stansbury AI" className="h-[46px] sm:h-12 flex-shrink-0" />
          </button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-600 font-['Outfit'] text-sm"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <Button onClick={() => navigate("/auth")} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none px-6 font-['Outfit'] text-sm" data-testid="cs-start-audit">Begin Your Blueprint</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0F172A]/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-36">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#818CF8] mb-6">Winning with AI</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-medium tracking-tighter text-white mb-8 leading-[1.1] max-w-3xl">
              An EMD Transforms Itself Amid Intense Volatility
            </h1>
            <img src="/quantus-logo-white.png" alt="Quantus" className="h-10 sm:h-12" />
          </motion.div>
        </div>
      </section>

      {/* Impact Strip */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-6">The Impact</p>
          <div className="flex flex-wrap gap-6 md:gap-12">
            {[
              { stat: "+34%", desc: "revenue increase within first 120 days" },
              { stat: "2.3x", desc: "more qualified investor meetings" },
              { stat: "~40%", desc: "faster sales cycles" },
              { stat: "3.2x", desc: "ROI on AI implementation investment" },
            ].map((item) => (
              <div key={item.stat} className="min-w-[140px]">
                <div className="text-3xl sm:text-4xl font-['Outfit'] font-medium text-[#0F172A] mb-1">{item.stat}</div>
                <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 md:px-12 py-16">

        {/* Executive Summary */}
        <div className="bg-white border border-slate-200 p-8 mb-12">
          <h3 className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#0033CC] mb-4">Executive Summary</h3>
          <ul className="space-y-3">
            {[
              "Installed an Agentic AI Growth Engine to replace manual investor outreach",
              "Achieved +34% revenue growth in 120 days — without adding headcount",
              "Increased qualified investor meetings by 2.3x and cut sales cycle time by ~40%",
              "Built a predictable, always-on capital-raising system",
              "Shifted from advisor-dependent growth to system-driven revenue generation",
            ].map((item, i) => (
              <li key={`idx-${i}`} className="flex items-start gap-3 text-sm font-['IBM_Plex_Sans'] text-slate-700">
                <CheckCircle className="w-4 h-4 text-[#0033CC] mt-0.5 flex-shrink-0" />{item}
              </li>
            ))}
          </ul>
        </div>

        {/* The Moment Everything Broke */}
        <SectionHeading>The Moment Everything Broke</SectionHeading>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">The firm had strong deals. Strong relationships. Strong reputation.</p>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">But growth stalled.</p>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">Pipeline volatility became the norm. Some weeks were full. Others were empty. Advisors were spending more time chasing leads than closing capital. Follow-ups lagged. Opportunities slipped.</p>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">The problem wasn't demand.</p>
        <p className="text-base font-['Outfit'] font-medium text-[#0F172A] mb-4">The problem was infrastructure.</p>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">Investor acquisition was manual. Qualification was inconsistent. Conversion depended on timing — and timing was unpredictable.</p>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed">Revenue wasn't scaling because the system wasn't built to scale.</p>

        {/* The Strategic Decision */}
        <SectionHeading>The Strategic Decision</SectionHeading>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">Leadership made a decisive shift:</p>
        <Quote>Stop hiring for growth. Start building systems for growth.</Quote>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">Instead of expanding the sales team, they installed an <strong>Agentic AI Growth Engine</strong> — a system designed to acquire investors continuously, qualify them instantly, convert them efficiently, and re-engage them automatically.</p>
        <p className="text-base font-['Outfit'] font-medium text-[#0F172A]">The goal wasn't incremental improvement. The goal was transformation.</p>

        {/* What They Built */}
        <SectionHeading>What They Built</SectionHeading>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-8">This wasn't a tool. This was an operating system for capital raising.</p>

        {/* Acquire */}
        <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-3 flex items-center gap-3"><span className="w-8 h-8 bg-[#E5EDFF] flex items-center justify-center text-xs font-['JetBrains_Mono'] text-[#0033CC]">1</span>Acquire — Always-On Investor Pipeline</h3>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">The firm deployed AI agents to scan and identify high-intent investors across LinkedIn, email databases, and behavioral signals. Outreach no longer depended on advisors "finding time." It ran continuously. Every day, new investors entered the pipeline — pre-qualified and context-aware.</p>
        <Outcome>A consistent flow of high-quality investor conversations, without manual prospecting.</Outcome>

        {/* Convert */}
        <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-3 flex items-center gap-3"><span className="w-8 h-8 bg-[#E5EDFF] flex items-center justify-center text-xs font-['JetBrains_Mono'] text-[#0033CC]">2</span>Convert — Instant Engagement, Higher Close Rates</h3>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">Speed became the advantage. AI SDR agents engaged investors the moment they showed interest with personalized outreach, intelligent qualification, and seamless calendar booking. No delays. No missed windows.</p>
        <Outcome>2.3x increase in booked investor meetings. Higher conversion rates driven by relevance and timing.</Outcome>

        {/* Operate */}
        <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-3 flex items-center gap-3"><span className="w-8 h-8 bg-[#E5EDFF] flex items-center justify-center text-xs font-['JetBrains_Mono'] text-[#0033CC]">3</span>Operate — Eliminate Friction, Increase Focus</h3>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">The firm embedded AI directly into its CRM: automated follow-ups triggered by behavior, dynamic lead scoring that prioritized high-value prospects, and automatic reactivation of cold leads with tailored messaging. Advisors stopped managing tasks. They started closing deals.</p>
        <Outcome>A shift from operational overhead to revenue-generating activity.</Outcome>

        {/* Expand */}
        <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-3 flex items-center gap-3"><span className="w-8 h-8 bg-[#E5EDFF] flex items-center justify-center text-xs font-['JetBrains_Mono'] text-[#0033CC]">4</span>Expand — Turn Investors into Repeat Capital</h3>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">The system didn't stop at the first investment. It extended into lifecycle management: automated deal updates, capital call reminders, and personalized reinvestment campaigns. Every investor became part of a structured growth loop.</p>
        <Outcome>Higher lifetime value and faster reinvestment cycles.</Outcome>

        {/* Defend */}
        <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-3 flex items-center gap-3"><span className="w-8 h-8 bg-[#E5EDFF] flex items-center justify-center text-xs font-['JetBrains_Mono'] text-[#0033CC]">5</span>Defend — Build a Data Advantage</h3>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">Every interaction fed back into the system: messaging improved, targeting sharpened, timing optimized. The firm wasn't just growing. It was learning.</p>
        <Outcome>A compounding advantage competitors couldn't replicate quickly.</Outcome>

        {/* How It Works */}
        <SectionHeading>How It Actually Works</SectionHeading>
        <div className="bg-[#0F172A] p-8 my-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: "Input Layer", items: ["CRM data", "Investor profiles", "Engagement signals"] },
              { label: "Decision Layer", items: ["Who to contact", "When to contact", "What to say"] },
              { label: "Execution Layer", items: ["Outreach", "Conversations", "Meeting booking"] },
              { label: "Feedback Loop", items: ["Every action", "improves the", "next action"] },
            ].map((col) => (
              <div key={col.label}>
                <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#818CF8] mb-2">{col.label}</p>
                {col.items.map((item, i) => (
                  <p key={`idx-${i}`} className="text-xs font-['IBM_Plex_Sans'] text-slate-400">{item}</p>
                ))}
              </div>
            ))}
          </div>
          <p className="text-sm font-['Outfit'] font-medium text-white mt-6">This is not automation. This is autonomous revenue infrastructure.</p>
        </div>

        {/* Execution Timeline */}
        <SectionHeading>Execution Timeline</SectionHeading>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-8">
          <PhaseCard phase="0-30" title="Activate" items={["Launched AI outreach campaigns", "Turned dormant CRM data into active pipeline", "Deployed initial qualification workflows"]} result="Immediate lift in investor interest and booked calls" />
          <PhaseCard phase="30-90" title="Systemize" items={["Integrated AI SDR workflows", "Built automated nurturing sequences", "Refined messaging based on real data"]} result="Rapid acceleration in deal flow and conversion" />
          <PhaseCard phase="90-120" title="Scale" items={["Expanded outreach across channels", "Optimized targeting and segmentation", "Increased automation depth"]} result="Compounding growth in capital inflows" />
        </div>

        {/* What Changed */}
        <SectionHeading>What Changed</SectionHeading>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
          <div className="border border-slate-200 bg-white p-6">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400 mb-4">Before</p>
            {["Pipeline depended on individual effort", "Outreach was inconsistent", "Follow-ups were delayed", "Growth was unpredictable"].map((item, i) => (
              <p key={`idx-${i}`} className="text-sm font-['IBM_Plex_Sans'] text-slate-500 mb-2 flex items-center gap-2"><span className="w-1.5 h-1.5 bg-red-400 flex-shrink-0" />{item}</p>
            ))}
          </div>
          <div className="border border-[#0033CC]/20 bg-[#E5EDFF]/30 p-6">
            <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC] mb-4">After</p>
            {["Pipeline runs automatically", "Outreach is continuous and targeted", "Follow-ups are instant and personalized", "Growth is predictable and scalable"].map((item, i) => (
              <p key={`idx-${i}`} className="text-sm font-['IBM_Plex_Sans'] text-[#0F172A] mb-2 flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#0033CC] flex-shrink-0" />{item}</p>
            ))}
          </div>
        </div>

        {/* Strategic Insight */}
        <Quote>The future of capital raising is not relationship-first. It is system-first, relationship-amplified.</Quote>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">The firms that win will not be the ones with the most advisors. They will be the ones with the fastest, smartest, always-on growth engines.</p>

        {/* Bottom Line */}
        <SectionHeading>Bottom Line</SectionHeading>
        <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-4">The Agentic AI Growth Engine didn't just improve performance.</p>
        <p className="text-base font-['Outfit'] font-medium text-[#0F172A] mb-6">It replaced the growth model.</p>
        <div className="bg-[#0F172A] p-8 my-8">
          <div className="grid grid-cols-3 gap-4 text-center mb-6">
            {[["Manual", "Automated"], ["Reactive", "Proactive"], ["Inconsistent", "Predictable"]].map(([from, to]) => (
              <div key={from}>
                <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] line-through mb-1">{from}</p>
                <p className="text-sm text-white font-['Outfit'] font-medium">{to}</p>
              </div>
            ))}
          </div>
          <p className="text-lg font-['Outfit'] font-medium text-white text-center leading-relaxed">
            More investors. Faster decisions.<br />Higher revenue. Zero bottlenecks.
          </p>
        </div>
      </article>

      {/* CTA */}
      <section className="bg-[#0F172A] py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#818CF8] mb-4">Ready?</p>
          <h2 className="text-2xl sm:text-3xl font-['Outfit'] font-medium text-white mb-4">Transform your business with AI</h2>
          <p className="text-sm text-slate-400 font-['IBM_Plex_Sans'] mb-8 max-w-lg mx-auto">The AI Growth Engine transforms your business into a system that acquires customers, converts revenue, and operates efficiently — automatically.</p>
          <Button onClick={() => navigate("/auth")} className="bg-white text-[#0F172A] hover:bg-slate-100 rounded-none px-10 py-3 h-12 text-base font-['Outfit'] font-medium" data-testid="cs-cta-start">
            Begin Your Blueprint <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row justify-between items-center gap-4">
          <img src="/stansbury-logo-white.png" alt="Stansbury AI" className="h-10" />
          <p className="text-xs text-slate-500 font-['IBM_Plex_Sans']"><a href="/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</a> · Confidential & Secure</p>
        </div>
      </footer>
      <SalesChatBot />
    </div>
  );
}
